// Ban log for one app, shown in the app card: mark a ban by hand, write what
// happened, attach screenshots, lift it later. The rows survive the lifting —
// `apps.is_banned` only ever says "banned right now".
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './Button';
import {
  clearAppBan, createAppBan, deleteAppBanScreenshot, getAppBanScreenshotImage,
  getAppBans, updateAppBan, uploadAppBanScreenshot,
  type AppBan, type AppBanScreenshot,
} from '../api/app-bans';
import { formatDateTime } from '@/lib/datetime';

const ACCEPTED = 'image/png,image/jpeg,image/gif,image/webp';

/** The bytes have no public link — they come through the authorized client. */
function BanScreenshot({ shot, onRemove }: { shot: AppBanScreenshot; onRemove?: () => void }) {
  const [src, setSrc] = useState('');
  const [full, setFull] = useState(false);
  const query = useQuery({
    queryKey: ['app-ban-screenshot', shot.id],
    queryFn: () => getAppBanScreenshotImage(shot.id),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);

  return (
    <>
      <div className="relative group">
        {query.isError ? (
          <div className="h-24 w-24 flex items-center justify-center rounded border border-border bg-danger-soft text-danger text-[11px]">
            Не открылся
          </div>
        ) : src ? (
          <button type="button" onClick={() => setFull(true)} title={shot.filename || 'Скриншот'}>
            <img src={src} alt={shot.filename || 'Скриншот бана'} className="h-24 w-24 object-cover rounded border border-border" />
          </button>
        ) : (
          <div className="h-24 w-24 rounded border border-border bg-surface-muted animate-pulse" />
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            title="Удалить скриншот"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-danger text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ×
          </button>
        )}
      </div>

      {full && src && (
        <button
          type="button"
          onClick={() => setFull(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
        >
          <img src={src} alt={shot.filename || 'Скриншот бана'} className="max-h-full max-w-full object-contain" />
        </button>
      )}
    </>
  );
}

function BanCard({
  ban, canEdit, onChanged,
}: { ban: AppBan; canEdit: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ban.reason);
  const [error, setError] = useState('');
  const open = !ban.cleared_at;

  useEffect(() => { if (!editing) setDraft(ban.reason); }, [ban.reason, editing]);

  const fail = (e: unknown) => setError(e instanceof Error ? e.message : 'Не получилось');

  const save = useMutation({
    mutationFn: () => updateAppBan(ban.id, { reason: draft.trim() }),
    onSuccess: () => { setEditing(false); setError(''); onChanged(); },
    onError: fail,
  });

  const clear = useMutation({
    mutationFn: (note: string) => clearAppBan(ban.id, note || undefined),
    onSuccess: () => { setError(''); onChanged(); },
    onError: fail,
  });

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      // One at a time: each response carries the ban with its list refreshed,
      // and a parallel burst would race on that same row.
      for (const file of Array.from(files)) await uploadAppBanScreenshot(ban.id, file);
    },
    onSuccess: () => { setError(''); onChanged(); },
    onError: fail,
  });

  const removeShot = useMutation({
    mutationFn: (shotId: string) => deleteAppBanScreenshot(shotId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: fail,
  });

  const author = ban.created_by_name || ban.created_by_email || (ban.source === 'auto' ? 'Автодетект' : '—');
  const clearedBy = ban.cleared_by_name || ban.cleared_by_email;

  return (
    <div className={`rounded-md border p-3 ${open ? 'border-danger/30 bg-danger-soft' : 'border-border bg-surface-muted/40'}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full ${open ? 'bg-danger text-white' : 'bg-surface-muted text-content-subtle'}`}>
          {open ? 'Активен' : 'Снят'}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-surface-muted text-content-subtle">
          {ban.source === 'auto' ? 'Автодетект стора' : 'Отметил тестер'}
        </span>
        <span className="text-content-subtle">Бан от {formatDateTime(ban.detected_at)}</span>
        <span className="text-content-subtle">· {author}</span>
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            rows={4}
            value={draft}
            maxLength={4000}
            onChange={(e) => setDraft(e.target.value)}
            className="block w-full px-2 py-1.5 text-sm border border-border-strong rounded-md bg-surface text-content focus:outline-none focus:ring-primary focus:border-primary"
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!draft.trim()}
            >
              Сохранить
            </Button>
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft(ban.reason); }}
              className="text-xs text-content-subtle hover:underline"
            >
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-content whitespace-pre-wrap">{ban.reason}</p>
      )}

      {ban.screenshots.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {ban.screenshots.map((shot) => (
            <BanScreenshot
              key={shot.id}
              shot={shot}
              onRemove={canEdit ? () => removeShot.mutate(shot.id) : undefined}
            />
          ))}
        </div>
      )}

      {!ban.cleared_at && canEdit && !editing && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline">
            Дополнить описание
          </button>
          <label className="text-xs text-primary hover:underline cursor-pointer">
            {upload.isPending ? 'Загружаю…' : '+ Скриншоты'}
            <input
              type="file"
              accept={ACCEPTED}
              multiple
              className="hidden"
              disabled={upload.isPending}
              onChange={(e) => {
                if (e.target.files?.length) upload.mutate(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            disabled={clear.isPending}
            onClick={() => {
              const note = window.prompt('Что изменилось? (необязательно)') ?? '';
              clear.mutate(note.trim());
            }}
            className="text-xs text-content-muted hover:text-content hover:underline disabled:opacity-50"
          >
            {clear.isPending ? 'Снимаю…' : 'Снять бан'}
          </button>
        </div>
      )}

      {ban.cleared_at && (
        <p className="mt-2 text-xs text-content-subtle">
          Снят {formatDateTime(ban.cleared_at)}
          {clearedBy ? ` · ${clearedBy}` : ''}
          {ban.cleared_note ? ` — ${ban.cleared_note}` : ''}
        </p>
      )}

      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  );
}

export function AppBanPanel({
  appId, canEdit,
}: { appId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [detectedAt, setDetectedAt] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['app-bans', appId],
    queryFn: () => getAppBans({ app_id: appId, limit: 50 }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['app-bans', appId] });
    // The flag, the Banned filter and the row tint all read the apps list.
    qc.invalidateQueries({ queryKey: ['apps'] });
  };

  const create = useMutation({
    mutationFn: () => createAppBan({
      app_id: appId,
      reason: reason.trim(),
      // A date-only value is a valid SQLite datetime; the tester usually enters
      // yesterday's ban today.
      detected_at: detectedAt || undefined,
    }),
    onSuccess: () => { setReason(''); setDetectedAt(''); setError(''); refresh(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Не получилось отметить бан'),
  });

  const bans = data?.bans ?? [];
  const openBan = bans.find((b) => !b.cleared_at);

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-content">Баны</h3>
        <span className="text-xs text-content-subtle">
          {bans.length > 0 ? `${bans.length} записей, ${openBan ? 'бан активен' : 'активных нет'}` : ''}
        </span>
      </div>

      {canEdit && !openBan && (
        <div className="mt-3 space-y-2">
          <textarea
            rows={3}
            value={reason}
            maxLength={4000}
            placeholder="Что произошло: за что забанили, что написали в письме, где это видно"
            onChange={(e) => setReason(e.target.value)}
            className="block w-full px-2 py-1.5 text-sm border border-border-strong rounded-md bg-surface text-content focus:outline-none focus:ring-primary focus:border-primary"
          />
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="ban-detected-at" className="block text-xs text-content-subtle mb-1">
                Дата бана
              </label>
              <input
                id="ban-detected-at"
                type="date"
                value={detectedAt}
                onChange={(e) => setDetectedAt(e.target.value)}
                className="px-2 py-1.5 text-sm border border-border-strong rounded-md bg-surface text-content focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <Button
              type="button"
              variant="danger"
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!reason.trim()}
            >
              Отметить бан
            </Button>
            <p className="text-xs text-content-subtle">
              Скриншоты можно приложить сразу после — к самой записи.
            </p>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {isLoading ? (
          <p className="text-xs text-content-subtle">Загрузка…</p>
        ) : bans.length === 0 ? (
          <p className="text-xs text-content-subtle">Банов не было.</p>
        ) : (
          bans.map((ban) => (
            <BanCard key={ban.id} ban={ban} canEdit={canEdit} onChanged={refresh} />
          ))
        )}
      </div>
    </div>
  );
}
